import {
  Modal,
  Tabs,
  Form,
  Input,
  Button,
  Space,
  Typography,
  message,
  Select,
  notification,
  Divider,
  Switch,
  Tag,
} from "antd";

import { useEffect, useState } from "react";
import { ApiService } from "api/api.service";
import { AuthType, ProfileType } from "dtos/email.dto";
import { useParams } from "react-router-dom";
import { useWatch } from "antd/es/form/Form";
import { useQueryClient } from "@tanstack/react-query";
import { TbFileTypePdf, TbKey, TbMail, TbSettings, TbUser, TbUsersGroup, TbX } from "react-icons/tb";
import { formatBytes, getContentTypeFromFile } from "utils/functions";
import z from "zod";

const { TabPane } = Tabs;
const { Title, Text } = Typography;

interface ConnectAccountModalProps {
  open: boolean;
  onClose: () => void;
  refetchEmails?: () => void;
  refetchProfile: () => void;
  setSelectedEmail: () => void;
}

export default function ConnectAccountModal({
  open,
  onClose,
  refetchEmails,
  refetchProfile,
  setSelectedEmail,
}: Readonly<ConnectAccountModalProps>) {
  const { app_key } = useParams();
  const { orgCode } = useParams();
  const [form] = Form.useForm();
  const [smtpForm] = Form.useForm();
  const [serviceForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState<"manual" | "smtp">("manual");
  const [parsedFileData, setParsedFileData] = useState<any>(null);
  const [isMicrosoftSelected, setIsMicrosoftSelected] = useState(false);
  const [isGoogleSelected, setIsGoogleSelected] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const queryClient = useQueryClient();

  const manualAuth = ApiService.email.useManualAuth();

  const handleManualSubmit = async (values: any) => {
    const trimmedPassword = values.password.replace(/\s+/g, "")?.trim();
    manualAuth.mutate(
      {
        appKey: app_key,
        orgCode: orgCode,
        credentials: {
          ...values,
          appPassword: isGoogleSelected ? trimmedPassword : undefined,
          authType: isMicrosoftSelected ? AuthType.WORKSPACE : AuthType.APP_PASSWORD,
          provider: values.provider,
        },
      },
      {
        onSuccess: () => {
          message.success("Account connected successfully!");
          form.resetFields();
          onClose();
          refetchEmails?.();
          // refetchProfile();
          if (app_key) {
            queryClient.invalidateQueries({ queryKey: ["attached-profiles"] });
          } else if (orgCode) {
            queryClient.invalidateQueries({ queryKey: ["org-attached-profiles"] });
          }
          setSelectedEmail();

          notification.info({
            message: "Your email is being synced. This won’t take long—please refresh after 10–15 seconds.",
            showProgress: true,
            duration: 10,
          });
        },
      },
    );
  };
  const smtpHandleManualSubmit = (values: any) => {
    manualAuth.mutate(
      {
        appKey: app_key,
        credentials: {
          ...values,
          authType: ProfileType.SMTP,
          smtpTls: values.smtpTls === "yes",
          imapTls: values.imapTls === "yes",
        },
      },
      {
        onSuccess: () => {
          message.success("Account connected successfully!");
          smtpForm.resetFields();
          onClose();
          setActiveTab("manual");
          refetchEmails?.();
          refetchProfile();

          notification.info({
            message: "Your email is being synced. This won’t take long—please refresh after 10–15 seconds.",
            showProgress: true,
            duration: 10,
          });
        },
      },
    );
  };
  const handleClose = () => {
    form.resetFields();
    setActiveTab("manual");
    onClose();
  };
  const provider = useWatch("provider", form);
  useEffect(() => {
    setIsMicrosoftSelected(provider === ProfileType.MICROSOFT);
    setIsGoogleSelected(provider === ProfileType.GOOGLE);

    form.setFieldsValue({ tenantId: "", clientId: "", clientSecret: "" });
    form.setFieldsValue({ password: "" });
  }, [provider]);
  // const allowedExtensions = ["pdf", "doc", "docx"];
  // const [status, setStatus] = useState("");
  // const allowedMimeTypes = [
  //   "application/pdf",
  //   "application/msword",
  //   "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  // ];
  // const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const fileArray = Array.from(files);

    const oversizedFiles = fileArray.filter((file) => file.size > 25 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      message.error(`Some files exceed 25MB limit: ${oversizedFiles.map((f) => f.name).join(", ")}`);
      event.target.value = "";
      return;
    }

    fileArray.forEach((file) => {
      const isJson = file.type === "application/json" || file.name.toLowerCase().endsWith(".json");

      if (!isJson) {
        message.error(`${file.name} is not a valid JSON file`);
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const parsed = JSON.parse(e.target?.result as string);

          // 🔐 STRICT FIELD VALIDATION
          if (!parsed.private_key || typeof parsed.private_key !== "string") {
            message.error(`${file.name}: Missing required field "private_key"`);
            return;
          }

          if (!parsed.client_email || typeof parsed.client_email !== "string") {
            message.error(`${file.name}: Missing required field "client_email"`);
            return;
          }

          // Optional extra safety (recommended)
          if (!parsed.private_key.includes("BEGIN PRIVATE KEY")) {
            message.error(`${file.name}: Invalid private key format`);
            return;
          }

          if (!parsed.client_email.includes("@")) {
            message.error(`${file.name}: Invalid client email`);
            return;
          }
          console.log({ parsed });
          setParsedFileData(parsed);

          // ✅ If everything passes
          setAttachedFiles((prev) => [...prev, file]);
          message.success(`${file.name} attached successfully`);
        } catch (err) {
          message.error(`${file.name} contains invalid JSON`);
        }
      };

      reader.readAsText(file);
    });

    event.target.value = "";
  };
  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
    // message.success("File removed");
  };
  //onSubmit upload files to server first make it formData
  const onServiceAccountSubmit = () => {
    if (attachedFiles.length === 0) {
      message.error("Please attach at least one file");
      return;
    }
    if (entries.length === 0) {
      message.error("You haven't added any email, please add a email to proceed");
      return;
    }
    const dataToSubmit = {
      serviceAccount: parsedFileData,
      entries,
      authType: AuthType.SERVICE_ACCOUNT,
      provider: ProfileType.GOOGLE,
    };
    manualAuth.mutate(
      {
        appKey: app_key,
        orgCode: orgCode,
        credentials: dataToSubmit as any,
      },
      {
        onSuccess: () => {
          message.success("Account connected successfully!");
          serviceForm.resetFields();
          onClose();
          refetchEmails?.();
          refetchProfile();
          setSelectedEmail();

          notification.info({
            message: "Your email is being synced. This won’t take long—please refresh after 10–15 seconds.",
            showProgress: true,
            duration: 10,
          });
        },
      },
    );

    // setAttachedFiles([]);
    // onClose();
  };
  const [current, setCurrent] = useState("");
  const [currentName, setCurrentName] = useState("");
  const [step, setStep] = useState<"email" | "name">("email");
  const [entries, setEntries] = useState<{ name: string; email: string }[]>([]);
  const [error, setError] = useState<string | null>(null);

  const addEmail = (name: string, email: string) => {
    email = email.trim();
    name = name.trim();
    const errorMessage = verify(email);

    if (errorMessage) {
      setError(errorMessage);
    } else {
      setEntries((prev) => [...prev, { name, email }]);
      setCurrent("");
      setCurrentName("");
    }
    setStep("email");
  };

  const removeEmail = (email: string) => {
    setEntries((prev) => prev.filter((e) => e.email !== email));
  };

  const verify = (text: string): string | null => {
    if (!z.string().email().safeParse(text).success) return "Please provide a valid email address";
    if (entries.some((e) => e.email === text)) {
      return "This mail is already selected";
    }
    return null;
  };

  const handleContinue = () => {
    const errMsg = verify(current);
    if (errMsg) {
      setError(errMsg);
      return;
    }
    setStep("name");
  };

  const handleBack = () => {
    setStep("email");
  };

  return (
    <Modal
      title={
        <Title level={4} className="mb-0">
          Connect Email Account
        </Title>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={520}
      centered
      destroyOnHidden
    >
      <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key as "manual" | "smtp")}>
        {/* OAuth Tab */}

        {/* Manual Login Tab */}
        <TabPane tab="Manual" icon={<TbKey />} key="manual">
          <Form
            form={form}
            initialValues={{ provider: ProfileType.GOOGLE }}
            layout="vertical"
            onFinish={handleManualSubmit}
          >
            <div className="h-[450px] overflow-y-auto">
              <Form.Item name="name" label="Full Name" rules={[{ required: true, message: "Please enter your name" }]}>
                <Input placeholder="Kevin Hart" autoComplete="name" />
              </Form.Item>
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input placeholder="you@example.com" autoComplete="email" />
              </Form.Item>
              <Form.Item label="Provider" name={"provider"} rules={[{ required: true }]}>
                <Select
                  options={[
                    { label: "Google", value: ProfileType.GOOGLE },
                    { label: "Microsoft", value: ProfileType.MICROSOFT },
                    // { label: "Yahoo", value: ProfileType.YAHOO },
                  ]}
                />
              </Form.Item>

              <Form.Item
                hidden={!isGoogleSelected}
                name="password"
                label={
                  <Space direction="vertical" size={0}>
                    <span> App Password</span>
                    {/* <Text type="secondary" className="text-xs font-normal">
                      For Gmail: use App Password | Outlook: use regular password
                    </Text> */}
                  </Space>
                }
                rules={[
                  {
                    required: isGoogleSelected,
                    message: "Please enter your password",
                  },
                ]}
                extra={
                  <Text type="secondary" className="text-xs">
                    <a
                      href="https://support.google.com/accounts/answer/185833"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500"
                    >
                      How to create Gmail App Password
                    </a>
                  </Text>
                }
              >
                <Input.Password placeholder="••••••••" autoComplete="current-password" />
              </Form.Item>
              <Form.Item
                rules={[
                  {
                    required: isMicrosoftSelected,
                    message: "Please enter your tenant ID",
                  },
                ]}
                hidden={!isMicrosoftSelected}
                name="tenantId"
                label="Tenant ID"
              >
                <Input placeholder="a1b2c3d4-e5f6-47a8-9b0c-1d2e3f4a5b6c" />
              </Form.Item>
              <Form.Item
                rules={[
                  {
                    required: isMicrosoftSelected,
                    message: "Please enter your client ID",
                  },
                ]}
                hidden={!isMicrosoftSelected}
                name="clientId"
                label="Client ID"
              >
                <Input placeholder="7f8e9d0a-1b2c-43d4-8e5f-6a7b8c9d0e1f" />
              </Form.Item>
              <Form.Item
                rules={[
                  {
                    required: isMicrosoftSelected,
                    message: "Please enter your client secret",
                  },
                ]}
                hidden={!isMicrosoftSelected}
                name="clientSecret"
                label="Client Secret"
              >
                <Input.Password placeholder="abc123~XyZ7890abcdefghijklmnopQRSTUV" autoComplete="current-password" />
              </Form.Item>
            </div>

            <Form.Item className="mb-0">
              <Space className="w-full justify-between">
                <Button onClick={handleClose} disabled={manualAuth.isPending}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" disabled={manualAuth.isPending} loading={manualAuth.isPending}>
                  Connect Account
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </TabPane>
        <TabPane tab="Service Account" icon={<TbUsersGroup />} key="service">
          {attachedFiles.length === 0 && (
            <label
              htmlFor="uploadFile1"
              className="bg-white text-slate-500 font-semibold text-base rounded w-full h-52 flex flex-col items-center justify-center cursor-pointer border-2 border-gray-300 border-dashed mx-auto"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-11 mb-3 fill-gray-500" viewBox="0 0 32 32">
                <path
                  d="M23.75 11.044a7.99 7.99 0 0 0-15.5-.009A8 8 0 0 0 9 27h3a1 1 0 0 0 0-2H9a6 6 0 0 1-.035-12 1.038 1.038 0 0 0 1.1-.854 5.991 5.991 0 0 1 11.862 0A1.08 1.08 0 0 0 23 13a6 6 0 0 1 0 12h-3a1 1 0 0 0 0 2h3a8 8 0 0 0 .75-15.956z"
                  data-original="#000000"
                />
                <path
                  d="M20.293 19.707a1 1 0 0 0 1.414-1.414l-5-5a1 1 0 0 0-1.414 0l-5 5a1 1 0 0 0 1.414 1.414L15 16.414V29a1 1 0 0 0 2 0V16.414z"
                  data-original="#000000"
                />
              </svg>
              Upload file
              <input
                onChange={handleFileSelect}
                type="file"
                id="uploadFile1"
                className="hidden"
                accept=".json,application/json"
              />
              <p className="text-xs font-medium text-slate-400 mt-2">PDF files are allowed.</p>
            </label>
          )}

          <div className="space-y-3 max-h-[320px] overflow-y-auto">
            {attachedFiles.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <Typography.Text strong className="text-sm">
                    {attachedFiles.length} file(s) attached
                  </Typography.Text>
                </div>
                <div className="space-y-2">
                  {attachedFiles.map((file, index) => {
                    const type = getContentTypeFromFile(file);

                    return (
                      <div
                        key={index}
                        className={`flex items-center justify-between  p-2 rounded transition-colors bg-white`}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {type === "pdf" ? <TbFileTypePdf /> : null}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-700 truncate">{file.name}</div>
                            <div className="text-xs text-gray-500">{formatBytes(file.size)}</div>
                          </div>
                        </div>
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<TbX size={16} />}
                          onClick={() => handleRemoveFile(index)}
                          className="flex-shrink-0"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 max-h-64 overflow-y-auto my-2">
            {entries.map((e, currIdx) => (
              <div
                key={e.email}
                className="flex items-center border-0 border-b border-solid justify-between gap-2 p-2 border-gray-200 bg-white"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-800 truncate">
                    {e.name}{" "}
                    {entries.findIndex((p) => p.name === e.name) !== currIdx ? (
                      <Tag className="text-xs uppercase rounded-full" color="orange">
                        Duplicate
                      </Tag>
                    ) : null}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{e.email}</div>
                </div>
                <Button className="w-20 flex-shrink-0" danger onClick={() => removeEmail(e.email)}>
                  Remove
                </Button>
              </div>
            ))}
          </div>
          <div>
            {step === "email" ? (
              <>
                {currentName ? (
                  <Typography.Text type="secondary" className="text-xs pl-1 block mb-1">
                    <TbUser className="inline" /> {currentName}
                  </Typography.Text>
                ) : null}
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter organization email"
                    value={current}
                    prefix={<TbMail />}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") setError(null);
                      setError(verify(value));
                      setCurrent(value);
                    }}
                    onPressEnter={() => !verify(current) && handleContinue()}
                  />
                  <Button className="w-24" type="primary" disabled={Boolean(verify(current))} onClick={handleContinue}>
                    Continue
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Typography.Text type="secondary" className="text-xs pl-1 block mb-1">
                  <TbMail className="inline" /> {current}
                </Typography.Text>
                <div className="flex gap-2">
                  <Input
                    prefix={<TbUser />}
                    placeholder="Enter name"
                    value={currentName}
                    onChange={(e) => setCurrentName(e.target.value)}
                    onPressEnter={() => currentName.trim() && addEmail(currentName, current)}
                    autoFocus
                  />
                  <Button className="w-20" onClick={handleBack}>
                    Back
                  </Button>
                  <Button
                    className="w-20"
                    type="primary"
                    disabled={!currentName.trim()}
                    onClick={() => addEmail(currentName, current)}
                  >
                    Add
                  </Button>
                </div>
              </>
            )}
            <Typography.Text className="text-red-400 text-xs pl-1" hidden={!error || !current || step === "name"}>
              {error}
            </Typography.Text>
          </div>
          <div>
            <Button type="primary" onClick={onServiceAccountSubmit} block className="mt-4 ">
              Connect Accounts
            </Button>
          </div>
        </TabPane>
        <TabPane tab="Advanced" icon={<TbSettings />} key="smtp">
          <Form
            initialValues={{ imapTls: "no", smtpTls: "no" }}
            form={smtpForm}
            layout="vertical"
            onFinish={smtpHandleManualSubmit}
          >
            <div className="w-full h-[450px] overflow-y-auto">
              <Form.Item name="name" label="Full Name" rules={[{ required: true, message: "Please enter your name" }]}>
                <Input placeholder="Kevin Hart" autoComplete="name" />
              </Form.Item>
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input placeholder="username@example.com" autoComplete="email" />
              </Form.Item>
              <Title level={5} className="mt-4 mb-3">
                IMAP Configuration (Incoming)
              </Title>
              <Form.Item
                name="imapUsername"
                label="IMAP Username"
                rules={[{ required: true, message: "Please enter IMAP username" }]}
              >
                <Input placeholder="username@example.com" />
              </Form.Item>
              <Space.Compact className="w-full">
                <Form.Item
                  name="imapPassword"
                  label="IMAP Password"
                  rules={[{ required: true, message: "Please enter IMAP password" }]}
                  className="flex-1"
                >
                  <Input.Password placeholder="••••••••" />
                </Form.Item>
                <Form.Item
                  name="imapTls"
                  label="Imap TLS"
                  rules={[{ required: true, message: "Port required" }]}
                  className="w-24 mb-0"
                >
                  <Select
                    options={[
                      { label: "Yes", value: "yes" },
                      { label: "No", value: "no" },
                    ]}
                  />
                </Form.Item>
              </Space.Compact>
              <Space.Compact className="w-full">
                <Form.Item
                  name="imapHost"
                  label="IMAP Host"
                  rules={[{ required: true, message: "Please enter IMAP host" }]}
                  className="flex-1 mb-0"
                >
                  <Input placeholder="imap.example.com" />
                </Form.Item>

                <Form.Item
                  name="imapPort"
                  label="Port"
                  rules={[{ required: true, message: "Port required" }]}
                  className="w-24 mb-0"
                >
                  <Input placeholder="imap port" />
                </Form.Item>
              </Space.Compact>
              <Title level={5} className="mt-4 mb-3">
                SMTP Configuration (Outgoing)
              </Title>
              <Form.Item
                name="smtpUsername"
                label="SMTP Username"
                rules={[{ required: true, message: "Please enter SMTP username" }]}
              >
                <Input placeholder="username@example.com" />
              </Form.Item>
              <Space.Compact className="w-full">
                <Form.Item
                  name="smtpPassword"
                  label="SMTP Password"
                  rules={[{ required: true, message: "Please enter SMTP password" }]}
                  className="flex-1"
                >
                  <Input.Password placeholder="••••••••" />
                </Form.Item>
                <Form.Item
                  name="smtpTls"
                  label="Smtp TLS"
                  rules={[{ required: true, message: "Port required" }]}
                  className="w-24 mb-0"
                >
                  <Select
                    options={[
                      {
                        label: "Yes",
                        value: "yes",
                      },
                      {
                        label: "No",
                        value: "no",
                      },
                    ]}
                  />
                </Form.Item>
              </Space.Compact>
              <Space.Compact className="w-full">
                <Form.Item
                  name="smtpHost"
                  label="SMTP Host"
                  rules={[{ required: true, message: "Please enter SMTP host" }]}
                  className="flex-1 mb-0"
                >
                  <Input placeholder="smtp.example.com" />
                </Form.Item>

                <Form.Item
                  name="smtpPort"
                  label="Port"
                  rules={[{ required: true, message: "Port required" }]}
                  className="w-24 mb-0"
                >
                  <Input placeholder="smtp port" />
                </Form.Item>
              </Space.Compact>
            </div>

            <Form.Item className="mb-0 mt-4">
              <Space className="w-full justify-between">
                <Button onClick={handleClose} disabled={manualAuth.isPending}>
                  Cancel
                </Button>
                <Button
                  onClick={smtpHandleManualSubmit}
                  type="primary"
                  htmlType="submit"
                  loading={manualAuth.isPending}
                >
                  Connect Account
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </TabPane>
      </Tabs>
    </Modal>
  );
}
